<template>
  <CCardBody>
    <!-- VoIP Disabled Alert -->
    <CAlert v-if="serviceDisabled" color="warning" class="mb-3">
      <CIcon name="cil-ban" class="mr-2" />
      <strong>VoIP service is disabled</strong>
      <div><small>Please contact administrator to enable VoIP service.</small></div>
    </CAlert>

    <template v-else>
      <!-- Filters -->
      <CCard v-if="false" class="filters" accent-color="warning">
        <CCardHeader role="button" class="text-warning shadow-none card-header" @click="resetFilters">
          <strong class="m-0"><CIcon :name="`cil-filter${$store.state.filterShow ? '-x' : ''}`" /> Filters</strong>
          <div class="card-header-actions">
            <CIcon :name="`cil-chevron-${$store.state.filterShow ? 'bottom' : 'top'}`" />
          </div>
        </CCardHeader>

        <CCollapse :show="$store.state.filterShow">
          <CCardBody class="p-2 d-flex flex-column flex-gap-2">
            <div class="d-flex flex-lg-row flex-column flex-gap-2">
              <!-- Status Filter -->
              <v-select
                class="v-select-filter w-100"
                placeholder="Status.."
                v-model="selectedStatus"
                :options="[
                  { value: 'Idle', label: 'Idle' },
                  { value: 'Online', label: 'Online' },
                  { value: 'Unavailable', label: 'Unavailable' },
                  { value: 'Offline', label: 'Offline' },
                ]"
                :searchable="false"
                :reduce="(o) => o.value"
                multiple
                @input="inputFilter"
              />

              <!-- Out of Service Filter -->
              <v-select
                class="v-select-filter w-100"
                placeholder="Out of Service.."
                v-model="selectedOutOfService"
                :options="[
                  { value: 'yes', label: 'Out of Service' },
                  { value: 'no', label: 'In Service' },
                ]"
                :searchable="false"
                :reduce="(o) => o.value"
                multiple
                @input="inputFilter"
              />

              <!-- User Assignment Filter -->
              <v-select
                class="v-select-filter w-100"
                placeholder="User Assignment.."
                v-model="selectedUserAssignment"
                :options="[
                  { value: 'assigned', label: 'Assigned to User' },
                  { value: 'unassigned', label: 'Not Assigned' },
                ]"
                :searchable="false"
                :reduce="(o) => o.value"
                multiple
                @input="inputFilter"
              />
            </div>
          </CCardBody>
        </CCollapse>
      </CCard>

      <CDataTable
        hover
        :items="loadedItems"
        :fields="fields"
        :loading="loading"
        :noItemsView="{
          noResults: 'No filtering results are available!',
          noItems: 'No VoIP accounts found!',
        }"
      >
        <!-- Extension -->
        <template #extension="{ item }">
          <td>
            <strong class="text-primary">#{{ item.extension }}</strong>
            <div class="small text-muted">{{ item.account_type }}</div>
          </td>
        </template>

        <!-- Fullname -->
        <template #fullname="{ item }">
          <td>
            <div class="font-weight-bold">{{ item.fullname }}</div>
            <div class="small text-muted">{{ item.status }}</div>
          </td>
        </template>

        <!-- User Assignment -->
        <template #user_assignment="{ item }">
          <td>
            <div v-if="item.user">
              <div class="d-flex align-items-center flex-gap-2">
                <CIcon name="cil-user" class="text-success" />
                <div>
                  <div class="font-weight-bold">{{ item.user.first_name }}</div>
                  <div class="small text-muted">{{ item.user.email }}</div>
                  <div class="small">
                    <CBadge v-for="role in item.user.roles" :key="role" color="info" class="mr-1">
                      {{ role }}
                    </CBadge>
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              <div class="d-flex align-items-center flex-gap-2">
                <CLink class="vertical-center" @click="assignUser(item)">
                  <CIcon name="cil-user-follow" class="text-muted" />
                </CLink>
                <div class="small text-muted">Not Assigned</div>
              </div>
            </div>
          </td>
        </template>

        <!-- Out of Service -->
        <template #out_of_service="{ item }">
          <td class="text-center">
            <CBadge :color="item.out_of_service === 'yes' ? 'danger' : 'success'">
              {{ item.out_of_service === 'yes' ? 'Out of Service' : 'In Service' }}
            </CBadge>
          </td>
        </template>

        <!-- DND Status -->
        <template #dnd_status="{ item }">
          <td class="text-center">
            <CSpinner v-if="item.updating" size="sm" color="warning" class="mb-1" />
            <CSwitch
              v-else
              labelOn="ON"
              labelOff="OFF"
              color="warning"
              shape="pill"
              :checked.sync="item.is_dnd_enabled"
              @update:checked="() => updateDndStatus(item)"
            />
          </td>
        </template>
      </CDataTable>

      <!-- Actions -->
      <CCard class="actions sticky-bottom">
        <CCardBody class="p-2">
          <div class="d-flex flex-wrap align-items-center flex-gap-3">
            <h5 class="mt-1">
              Total: <span class="d-inline count bg-primary pb-1">{{ total }}</span>
            </h5>
          </div>
        </CCardBody>
      </CCard>
    </template>

    <mc-spinner :opacity="0.8" v-show="loading" :mtop="32 + mcSpinnerMarginTop + 'px'" />
  </CCardBody>
</template>

<script>
import _debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';

export default {
  name: 'VoIPBackendTable',
  data() {
    return {
      loadedItems: [],
      fields: [
        { key: 'extension', label: 'Extension', sorter: false, _style: 'min-width: 120px; width: 140px;' },
        { key: 'fullname', label: 'Account', sorter: false, _style: 'min-width: 200px; width: 250px;' },
        { key: 'user_assignment', label: 'User Assignment', sorter: false, _style: 'min-width: 250px;' },
        { key: 'out_of_service', label: 'Service Status', sorter: false, _style: 'min-width: 140px; width: 140px; text-align: center;' },
        { key: 'dnd_status', label: 'Do Not Disturb', sorter: false, _style: 'min-width: 140px; width: 140px; text-align: center;' },
      ],

      total: 0,
      loading: false,

      // Filters
      selectedStatus: [],
      selectedOutOfService: [],
      selectedUserAssignment: [],

      // Element Cover Spinner
      mcSpinnerMarginTop: 0,
    };
  },

  async mounted() {
    await this.getAllItems();
  },

  computed: {
    ...mapGetters(['voipEnabled']),
    serviceDisabled: {
      get() { return this.voipEnabled === false; },
      set(value) { this.$store.commit('setVoipEnabled', !value); }
    },
    userExtension() { return this.authUser?.user?.voip_extension; },
  },

  methods: {
    async getAllItems() {
      this.loading = true;

      try {
        const params = {
          options: 'extension,account_type,fullname,out_of_service,status',
          include_dnd: 1,

          status: this.selectedStatus,
          'out-of-service': this.selectedOutOfService,
          'user-assignment': this.selectedUserAssignment,
        };

        const response = await this.$axios.get(this.$backend.VOIP.ACCOUNTS, { params });
        const data = response.data;

        if (data.success) {
          this.loadedItems = data.accounts.map(item => ({
            ...item,
            updating: false,
            _classes: item.status === 'Unavailable' ? 'table-danger grayout' : (item.is_dnd_enabled ? 'table-warning' : ''),
          }));

          this.total = data.accounts.length;
          this.serviceDisabled = false;

          // Kullanıcının extension'ı için (voip) account status'ü güncelle
          if (this.userExtension) {
            const loggedInAccount = this.loadedItems.find(account => String(account.extension) === String(this.userExtension));
            this.$store.commit('setVoipAccountStatus', loggedInAccount?.status || null);
          } else {
            this.$store.commit('setVoipAccountStatus', null);
          }
        } else {
          this.$toast.error('Failed to load VoIP accounts');
        }
      } catch (error) {
        if (error?.response?.status === 503) {
          this.serviceDisabled = true;
        } else {
          this.$toast.error('Failed to load VoIP accounts');
        }
      } finally {
        this.loading = false;
      }
    },

    async updateDndStatus(item) {
      item.updating = true;

      try {
        const response = await this.$axios.post(this.$backend.VOIP.EXTENSION.DND, {
          extension: item.extension,
          status: item.is_dnd_enabled ? 'yes' : 'no'
        });

        if (response.data.success) {
          this.$toast.success('DND status updated successfully');
          item.dnd_status = item.is_dnd_enabled ? 'yes' : 'no';
        } else {
          this.$toast.error('Failed to update DND status');
          // Revert the change
          item.is_dnd_enabled = !item.is_dnd_enabled;
        }
      } catch (error) {
        console.error('Error updating DND status:', error);
        this.$toast.error('Failed to update DND status');
        // Revert the change
        item.is_dnd_enabled = !item.is_dnd_enabled;
      } finally {
        item.updating = false;
        item._classes = item.is_dnd_enabled ? 'table-warning' : '';
      }
    },

    assignUser(item) {
      this.$store.state.filters.users.selectedType = 'Admin';
      this.$router.push({ name: 'All Users' });
    },

    // Filters
    inputFilter: _debounce(function () {
      this.getAllItems();
    }, 300),

    resetFilters() {
      if (this.$store.state.filterShow) {
        this.selectedStatus = [];
        this.selectedOutOfService = [];
        this.selectedUserAssignment = [];
        this.getAllItems();
      }
      this.$store.commit('toggle', 'filterShow');
    },
  },
};
</script>

<style>
.table th,
.table td {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  vertical-align: middle;
}

.table th {
  border-top: none;
}
</style>
